import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { SAME_SITE_STRICT } from '@constants/cookies';
import { IS_DEV_NODE_ENV } from '@constants/nodeEnvs';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import Cookies from 'js-cookie'; // Import js-cookie

const DARK_MODE_COOKIE = 'xceed-dark-mode';
const cookieDomain = IS_DEV_NODE_ENV ? 'localhost' : '.xceed.me';
const LIGHT_THEME_COLOR = '#FFFFFF';
const DARK_THEME_COLOR = '#242426';

const updateThemeMeta = (isDark: boolean) => {
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = isDark ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
  }
};

export const DarkModeContext = createContext({
  isDarkMode: false,
  toggleDarkMode: (_e?: React.MouseEvent | React.KeyboardEvent) => {},
  isActive: false,
});

export const DarkModeProvider = ({ children }: { children: ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { isDarkModeEnabled: isActive } = useContext(FeatureFlagContext);

  const toggleDarkMode = (e?: React.MouseEvent | React.KeyboardEvent) => {
    const newDarkMode = !isDarkMode;

    const applyDarkMode = () => {
      setIsDarkMode(newDarkMode);

      if (newDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      updateThemeMeta(newDarkMode);

      Cookies.set(DARK_MODE_COOKIE, newDarkMode.toString(), {
        domain: cookieDomain,
        path: '/',
        sameSite: SAME_SITE_STRICT,
        secure: true,
        expires: 3650,
      });
    };

    const prefersReducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!document.startViewTransition || prefersReducedMotion) {
      applyDarkMode();
      return;
    }

    if (e) {
      let x: number, y: number;

      if ('clientX' in e && e.clientX !== 0) {
        x = e.clientX;
        y = e.clientY;
      } else {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }

      document.documentElement.style.setProperty('--vt-x', `${x}px`);
      document.documentElement.style.setProperty('--vt-y', `${y}px`);
    }

    document.startViewTransition(applyDarkMode);
  };

  const determineInitialDarkMode = () => {
    const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const darkModeCookie = Cookies.get(DARK_MODE_COOKIE); // Retrieve value from cookies

    if (isActive) {
      if (darkModeCookie === undefined) {
        return prefersDarkMode;
      } else {
        return darkModeCookie === 'true';
      }
    } else {
      return false;
    }
  };

  const initialDarkMode = determineInitialDarkMode();

  useEffect(() => {
    if (initialDarkMode !== isDarkMode) {
      setIsDarkMode(initialDarkMode);
      if (initialDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      updateThemeMeta(initialDarkMode);
    }
  }, [initialDarkMode, isDarkMode]);

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode, isActive }}>{children}</DarkModeContext.Provider>
  );
};
